import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MANAGER_ROLES, ROLE_CLIENT } from './api/auth';
import { TooltipProvider } from './ui/Tooltip';
import { Skeleton } from './ui/Skeleton';
import { isOwnersHost } from './lib/host';
// Auth pages stay eager: tiny, on every cold-load critical path.
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ClientLogin from './pages/client/ClientLogin';

// v2 (Owners.tax) shell. Mounted only when the browser is on the
// owners.tax domain family. Code-split so v1 visitors never download it.
const OwnersApp = lazy(() => import('./v2/OwnersApp'));

// Everything else is code-split. Cabinet pulls Three.js, DocumentView
// pulls PDF.js + mammoth + xlsx, Welcome pulls @react-three/fiber, Books
// pulls Recharts. Loading them on /login was a multi-hundred-KB tax for
// no reason.
const Welcome = lazy(() => import('./pages/public/Welcome'));
const Profile = lazy(() => import('./pages/Profile'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClientsList = lazy(() => import('./pages/ClientsList'));
const Deadlines = lazy(() => import('./pages/Deadlines'));
const ClientHub = lazy(() => import('./pages/manager/ClientHub'));
const Cabinet = lazy(() => import('./pages/manager/Cabinet'));
const Books = lazy(() => import('./pages/manager/Books'));
const DocumentView = lazy(() => import('./pages/manager/DocumentView'));
const Team = lazy(() => import('./pages/Team'));
const FirmSettings = lazy(() => import('./pages/FirmSettings'));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'));
const StaffInvite = lazy(() => import('./pages/StaffInvite'));
const EmailTemplatesPage = lazy(() => import('./pages/settings/EmailTemplatesPage'));
const QueuePanel = lazy(() => import('./pages/admin/QueuePanel'));
const FirmInbound = lazy(() => import('./pages/FirmInbound'));
const UploadLinkRoute = lazy(() => import('./pages/demo/UploadLinkRoute'));
const DemoGateway = lazy(() => import('./pages/demo/DemoGateway'));
const DemoComingSoon = lazy(() => import('./pages/demo/DemoComingSoon'));
const BookkeeperDemo = lazy(() => import('./pages/demo/BookkeeperDemo'));
const TaxProDemo = lazy(() => import('./pages/demo/TaxProDemo'));
const OperationsDemo = lazy(() => import('./pages/demo/OperationsDemo'));
const QboCallback = lazy(() => import('./pages/public/QboCallback'));
const Privacy = lazy(() => import('./pages/public/Privacy'));
const Terms = lazy(() => import('./pages/public/Terms'));
const ClientHome = lazy(() => import('./pages/client/ClientHome'));
const MyTaxes = lazy(() => import('./pages/MyTaxes'));

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] p-6">
      <div className="w-full max-w-md space-y-3">
        <Skeleton variant="text-lg" />
        <Skeleton variant="text" />
        <Skeleton variant="text" className="w-2/3" />
      </div>
    </div>
  );
}

function RequireManager({ children, ownerOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!MANAGER_ROLES.includes(user.role)) return <Navigate to="/" replace />;
  if (ownerOnly && user.role !== 'firm_owner') return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireClient({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/client/login" replace />;
  if (user.role !== ROLE_CLIENT) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Welcome />;
  if (MANAGER_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/client/home" replace />;
}

export default function App() {
  // Hostname-aware product surface. owners.tax family -> v2 shell;
  // bridgeinto.tax (and localhost) -> v1 shell.
  if (isOwnersHost()) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <OwnersApp />
        <Toaster position="bottom-right" richColors theme="dark" />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <TooltipProvider>
        <Router>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<RoleRedirect />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/staff-invite/:token" element={<StaffInvite />} />
              <Route path="/u/:token" element={<UploadLinkRoute />} />
              <Route path="/demo" element={<DemoGateway />} />
              <Route path="/demo/client" element={<Navigate to="/u/demo-client-token" replace />} />
              <Route path="/demo/bookkeeper" element={<BookkeeperDemo />} />
              <Route path="/demo/operations" element={<OperationsDemo />} />
              <Route path="/demo/tax-pro" element={<TaxProDemo />} />
              <Route path="/oauth/qbo/callback" element={<QboCallback />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              <Route path="/dashboard" element={<RequireManager><Dashboard /></RequireManager>} />
              <Route path="/clients" element={<RequireManager><ClientsList /></RequireManager>} />
              <Route path="/deadlines" element={<RequireManager><Deadlines /></RequireManager>} />
              <Route path="/clients/:id" element={<RequireManager><ClientHub /></RequireManager>} />
              <Route path="/clients/:id/tax" element={<RequireManager><Cabinet /></RequireManager>} />
              <Route path="/clients/:id/books" element={<RequireManager><Books /></RequireManager>} />
              <Route path="/documents/:id" element={<RequireManager><DocumentView /></RequireManager>} />
              <Route path="/profile" element={<RequireManager><Profile /></RequireManager>} />
              <Route path="/settings/team" element={<RequireManager ownerOnly><Team /></RequireManager>} />
              <Route path="/settings/firm" element={<RequireManager ownerOnly><FirmSettings /></RequireManager>} />
              <Route path="/settings/security" element={<RequireManager ownerOnly><SecuritySettings /></RequireManager>} />
              <Route path="/settings/email-templates" element={<RequireManager ownerOnly><EmailTemplatesPage /></RequireManager>} />
              <Route path="/admin/queue" element={<RequireManager ownerOnly><QueuePanel /></RequireManager>} />
              <Route path="/firm/inbound" element={<RequireManager><FirmInbound /></RequireManager>} />

              <Route path="/client/login" element={<ClientLogin />} />
              <Route path="/client/home" element={<RequireClient><ClientHome /></RequireClient>} />

              <Route path="/my-taxes" element={<MyTaxes />} />
            </Routes>
          </Suspense>
        </Router>
        <Toaster position="bottom-right" richColors theme="dark" />
      </TooltipProvider>
    </AuthProvider>
  );
}
